import type { RewardClaimBatchResult, RewardClaimResult, RewardOffer } from '../store/services/rewardsApi';

type ClaimRequest = {
  projectId: string; rewardId: string; sessionId?: string | null;
};
export type ClaimReward = (request: ClaimRequest) => { unwrap: () => Promise<RewardClaimResult> };
type BatchRequest = { projectId: string; rewardIds: string[]; sessionId?: string | null };
export type ClaimRewards = (request: BatchRequest) => { unwrap: () => Promise<RewardClaimBatchResult> };

const inFlightClaims = new Map<string, Promise<RewardClaimResult>>();
const claimKey = (offer: RewardOffer): string => `${offer.project_id}:${offer.id}`;

export const preciseCredits = (amount: string): string => {
  const [integer, decimal = ''] = amount.split('.');
  const fraction = decimal.replace(/0+$/, '');

  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`;
};

/** Add the server's decimal strings without rounding fractional credits. */
export const totalRewardCredits = (offers: RewardOffer[]): string => {
  const parts = offers.map(({ amount }) => amount.split('.'));
  const precision = Math.max(0, ...parts.map(([, fraction = '']) => fraction.length));
  const total = parts.reduce((sum, [integer, fraction = '']) => (
    sum + globalThis.BigInt(integer + fraction.padEnd(precision, '0'))
  ), globalThis.BigInt(0)).toString().padStart(precision + 1, '0');

  return preciseCredits(precision ? `${total.slice(0, -precision)}.${total.slice(-precision)}` : total);
};

/** A grouped claim and any still-mounted individual control share one request. */
export const claimRewardOnce = (
  claim: ClaimReward, offer: RewardOffer, sessionId: string | null | undefined, onGranted: () => void,
): Promise<RewardClaimResult> => {
  const key = claimKey(offer);
  const pending = inFlightClaims.get(key);

  if (pending) return pending;
  const request = claim({
    projectId: offer.project_id, rewardId: offer.id, sessionId: sessionId || offer.session_id,
  }).unwrap().then((result) => {
    if (result.already_claimed === false && result.reward.claimed_at
      && result.reward.id === offer.id && result.reward.project_id === offer.project_id) onGranted();

    return result;
  }).finally(() => {
    if (inFlightClaims.get(key) === request) inFlightClaims.delete(key);
  });

  inFlightClaims.set(key, request);

  return request;
};

const granted = (result: RewardClaimBatchResult, offer: RewardOffer): boolean => (
  result.already_claimed === false && result.rewards.some((reward) => (
    reward.id === offer.id && reward.project_id === offer.project_id && Boolean(reward.claimed_at)
  ))
);

/**
 * Claim a group in one request. Offers already in flight (an inline click a
 * moment earlier) are awaited rather than requested twice, and every offer in
 * the group shares the batch so a later inline click joins it instead.
 */
// eslint-disable-next-line max-statements
export const claimRewardsOnce = async (
  claim: ClaimRewards, offers: RewardOffer[], sessionId: string | null | undefined, onGranted: () => void,
): Promise<void> => {
  const pending = offers.map((offer) => inFlightClaims.get(claimKey(offer))).filter(Boolean);
  const fresh = offers.filter((offer) => !inFlightClaims.has(claimKey(offer)));

  if (fresh.length) {
    const projectId = fresh[0].project_id;
    const request = claim({
      projectId, rewardIds: fresh.map((offer) => offer.id), sessionId: sessionId || fresh[0].session_id,
    }).unwrap().then((result) => {
      if (fresh.some((offer) => granted(result, offer))) onGranted();

      return result;
    });

    fresh.forEach((offer) => {
      const key = claimKey(offer);
      const share = request.then((result) => ({
        already_claimed: result.already_claimed, credits_balance: result.credits_balance, message: result.message,
        reward: result.rewards.find((reward) => reward.id === offer.id) ?? offer,
      })).finally(() => { if (inFlightClaims.get(key) === share) inFlightClaims.delete(key); });

      // The batch caller reports the failure; a share nobody joined must not surface it again.
      share.catch(() => undefined);
      inFlightClaims.set(key, share);
    });
    await request;
  }
  await Promise.all(pending);
};
